import { MenuCategory, MenuItem, Size } from '@/models/menuModels';
import { BundleGroup, BundleItemDTO } from '@/models/bundleModels';

export const isBundleGroupFull = (itemCount: number, group: BundleGroup): boolean => itemCount >= group.maxAllowed;

// Working shapes used while matching/merging. We only type the fields this logic reads/mutates:
// arrays are mutable and `sort` is numeric, unlike the readonly, string-`sort`, getter-bearing
// MenuItem/Recipe instances. The full item data still rides along on the spread objects at runtime
// and is reconstructed via `new MenuItem(...)` at the end.
type BundleRecipe = { recipeId: string; recipePid: string; sizes: Size[] };
type BundleWorkingItem = { itemPid: string; webcatPid: string; sort: number; recipes: BundleRecipe[] };

export function mapBundleGroupItemsToMenuItems(locationMenu: MenuCategory[], bundleItems: ReadonlyArray<BundleItemDTO>): MenuItem[] {
    if (!locationMenu) {
        console.error('MENU FAIL');
        return [];
    }
    const menuItems = locationMenu.flatMap((c) => c.items);

    const matchedItems = bundleItems
        .map((groupItem): BundleWorkingItem | null => {
            const matchingItem = menuItems.find((i) => {
                return i.itemPid === String(groupItem.legacy_item_id) && i.webcatPid === String(groupItem.legacy_category_id);
            });
            if (!matchingItem) {
                return null;
            }
            const recipes: BundleRecipe[] = matchingItem.recipes
                .filter((r) => r.recipePid === String(groupItem.legacy_crust_id))
                .map((r) => ({
                    ...r,
                    sizes: r.sizes.filter((s) => s.externalId === String(groupItem.legacy_size_id)),
                }));

            return { ...matchingItem, sort: groupItem.sort, recipes };
        })
        .filter((i): i is BundleWorkingItem => !!i && i.recipes.filter((r) => r.sizes.length > 0).length > 0)
        .filter((i) => i.recipes.length > 0);

    // if group.items have the same itemPid and webcatPid, merge the recipes
    const mergedItems = matchedItems.reduce<BundleWorkingItem[]>((acc, item) => {
        const existingItem = acc.find((i) => i.itemPid === item.itemPid && i.webcatPid === item.webcatPid);
        if (existingItem) {
            if (item.sort > 0) {
                if (existingItem.sort > 0) {
                    existingItem.sort = existingItem.sort > item.sort ? item.sort : existingItem.sort;
                } else {
                    existingItem.sort = item.sort;
                }
            }
            item.recipes.forEach((r) => {
                const existingRecipe = existingItem.recipes.find((i) => i.recipeId === r.recipeId);
                if (existingRecipe) {
                    r.sizes.forEach((size) => {
                        const existingSize = existingRecipe.sizes.find((i) => i.sizeId === size.sizeId);
                        if (!existingSize) {
                            existingRecipe.sizes = existingRecipe.sizes.concat(size);
                        }
                    });
                } else {
                    existingItem.recipes = existingItem.recipes.concat(item.recipes);
                }
            });
            return acc;
        } else {
            return acc.concat(item);
        }
    }, []);

    return (
        mergedItems
            .sort((a, b) => a.sort - b.sort)
            // renumber sort sequentially due to collision issue on display; cast bridges the working
            // shape to the model's getter-bearing Partial<MenuItem> input (data is complete at runtime)
            .map((item, idx) => new MenuItem({ ...item, sort: String(idx), recipes: [...item.recipes].sort((a, b) => Number(a.recipePid) - Number(b.recipePid)) } as Partial<MenuItem>))
    );
}
